import {
  AppShell,
  AppShellBrand,
  AppShellDrawer,
  AppShellDrawerClose,
  AppShellDrawerContent,
  AppShellDrawerTrigger,
  AppShellMain,
  AppShellPanel,
  AppShellPanelBody,
  AppShellSidebar,
  AppShellSkipLink,
  AppShellUserMenu,
} from '@amore/ui/components/app-shell'
import { BrandWordmark } from '@amore/ui/components/brand'
import { DropdownMenuItem } from '@amore/ui/components/dropdown-menu'
import {
  Navigation,
  NavigationSection,
  NavigationSectionHeading,
  navigationItemStyles,
} from '@amore/ui/components/navigation'
import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import { LogOut } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { roleLabels } from '@/lib/labels'
import { usePrototype } from '@/mock/store'

type NavItem = { label: string; to: string }

export const NAV: { heading: string; items: NavItem[] }[] = [
  {
    heading: 'Vaše svatba',
    items: [
      { label: 'Přehled', to: '/' },
      { label: 'Úkoly', to: '/tasks' },
      { label: 'Hosté', to: '/guests' },
      { label: 'Dodavatelé', to: '/suppliers' },
      { label: 'Harmonogram', to: '/schedule' },
      { label: 'Rozpočet', to: '/budget' },
    ],
  },
  {
    heading: 'Plánovač',
    items: [
      { label: 'Místa', to: '/venues' },
      { label: 'Obřad', to: '/ceremony' },
      { label: 'Jídlo a pití', to: '/reception' },
      { label: 'Foto a video', to: '/photo' },
      { label: 'Hudba a zábava', to: '/soon/hudba-a-zabava' },
      { label: 'Vizážista a stylistka', to: '/soon/vizazista-a-stylistka' },
      { label: 'Vizuál a dekorace', to: '/soon/vizual-a-dekorace' },
      { label: 'Šaty, oblek a prsteny', to: '/soon/saty-oblek-a-prsteny' },
    ],
  },
  {
    heading: 'Svatební web',
    items: [
      { label: 'Web před svatbou', to: '/soon/web-pred-svatbou' },
      { label: 'Svatební den', to: '/soon/svatebni-den' },
    ],
  },
  { heading: 'Nastavení', items: [{ label: 'Historie a obnova', to: '/soon/historie-a-obnova' }] },
]

function isActive(pathname: string, to: string) {
  return to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`)
}

export function AppLayout({ children }: { children: ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false)
  return (
    <AppShellDrawer open={mobileOpen} onOpenChange={setMobileOpen}>
      <AppShell className="h-dvh overflow-hidden bg-background">
        <AppShellSkipLink href="#main-content">Přeskočit na obsah</AppShellSkipLink>
        <AppShellSidebar className="border-0 bg-background p-3">
          <Panel onNavigate={() => setMobileOpen(false)} />
        </AppShellSidebar>
        <AppShellDrawerContent title="Hlavní navigace">
          <Panel
            onNavigate={() => setMobileOpen(false)}
            closeAction={<AppShellDrawerClose label="Zavřít menu" />}
          />
        </AppShellDrawerContent>
        <div className="flex h-dvh min-w-0 flex-col overflow-hidden">
          <AppShellDrawerTrigger label="Otevřít menu" />
          <AppShellMain
            id="main-content"
            tabIndex={-1}
            className="flex min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain px-5 pb-14 sm:px-8 sm:pb-16 lg:pb-20"
          >
            {children}
          </AppShellMain>
        </div>
      </AppShell>
    </AppShellDrawer>
  )
}

function Panel({ onNavigate, closeAction }: { onNavigate: () => void; closeAction?: ReactNode }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const { data, role } = usePrototype()
  const navigate = useNavigate()
  const { brideName, groomName } = data.identity
  const weddingName = brideName && groomName ? `${brideName} a ${groomName}` : 'Moje svatba'
  const member = data.members[0]
  return (
    <AppShellPanel>
      <AppShellBrand brand={<BrandWordmark />} subtitle={weddingName} action={closeAction} />
      <AppShellPanelBody>
        <Navigation aria-label="Hlavní navigace">
          {NAV.map((section) => (
            <NavigationSection key={section.heading} className="space-y-0">
              <NavigationSectionHeading>{section.heading}</NavigationSectionHeading>
              <div className="space-y-0.5">
                {section.items.map((item) => (
                  <NavEntry
                    key={item.label}
                    item={item}
                    pathname={pathname}
                    onNavigate={onNavigate}
                  />
                ))}
              </div>
            </NavigationSection>
          ))}
        </Navigation>
      </AppShellPanelBody>
      <AppShellUserMenu
        name={member?.name ?? 'Anna Nováková'}
        caption={roleLabels[role]}
        avatarFallback={member?.initials ?? 'AN'}
      >
        <DropdownMenuItem
          className="text-accent-foreground"
          onSelect={() => void navigate({ to: '/login' })}
        >
          <LogOut className="size-4" strokeWidth={1.8} />
          Odhlásit se
        </DropdownMenuItem>
      </AppShellUserMenu>
    </AppShellPanel>
  )
}

function NavEntry({
  item,
  pathname,
  onNavigate,
}: {
  item: NavItem
  pathname: string
  onNavigate: () => void
}) {
  const active = isActive(pathname, item.to)
  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={navigationItemStyles({ active, activeAppearance: 'solid', size: 'sm' })}
    >
      <span className="truncate">{item.label}</span>
    </Link>
  )
}
